<template>
  <div
    v-intersect.once="onIntersect"
    :class="{
      'seminar-elearning-fi': true,
      'seminar-elearning-fi--rtl': dark,
    }"
  >
    <section-divider-fi
      size="small"
      :dark="dark"
    />
    <grid-container-fi>
      <div class="fi-col-start-1 fi-col-end-13 lg:fi-col-start-2 lg:fi-col-end-12">
        <grid-columns-fi :columns="isLG ? 10 : 12">
          <div
            class="seminar-elearning-fi__image fi-col-start-1 fi-col-end-13 md:fi-col-end-7 lg:fi-col-start-1 lg:fi-col-end-6"
          >
            <cloud-image-fi
              v-if="event.image"
              class="seminar-elearning-fi__img"
              :src="event.image"
            />
          </div>
          <div
            class="seminar-elearning-fi__content fi-col-start-1 fi-col-end-13 md:fi-col-start-8 lg:fi-col-start-7 lg:fi-col-end-11"
          >
            <div class="seminar-elearning-fi__info">
              <div
                v-if="event.start"
                class="seminar-elearning-fi__info-data date"
              >
                <icon-fi icon="calendar" />
                <span class="seminar-elearning-fi__info__text">
                  <template v-if="!event.followEvents">
                    {{ event.start.day }}
                  </template>
                  <template v-else>
                    {{ event.followEvents[0].start.dayShort }} -
                    {{ event.followEvents[event.followEvents.length - 1].end.day }}
                  </template>
                </span>
              </div>

              <div class="seminar-elearning-fi__info-data location">
                <icon-fi icon="map-pin" />
                <span class="seminar-elearning-fi__info__text">{{
                  event.location.type === 'onsite'
                    ? event.location.city
                    : $dict.get('seminars.Location.Online')
                }}</span>
              </div>

              <template v-if="!event.followEvents">
                <div
                  v-if="event.start"
                  class="seminar-elearning-fi__info-data time"
                >
                  <icon-fi icon="clock" />
                  <span class="seminar-elearning-fi__info__text"
                    >{{ event.start.time }} - {{ event.end.time }}</span
                  >
                </div>
              </template>
              <template v-else>
                <div class="seminar-elearning-fi__info-data time time-follow-events">
                  <icon-fi icon="clock" />
                  <div class="seminar-elearning-fi__info__follow-events">
                    <span
                      v-for="followEvent in event.followEvents"
                      :key="followEvent.id"
                      class="seminar-elearning-fi__info__text"
                    >
                      {{ followEvent.start.dayShort }} | {{ followEvent.start.time }} -
                      {{ followEvent.end.time }}
                    </span>
                  </div>
                </div>
              </template>

              <div class="seminar-elearning-fi__info-data price">
                <icon-fi icon="euro" />

                <loading-indicator
                  v-if="eventPriceLoading"
                  transparent
                  variant="spinnerLocal"
                  spinoff="in-relative-centered"
                  class="seminar-elearning-fi__info-data__loading-wrapper"
                />
                <span
                  v-else
                  class="seminar-elearning-fi__info__text"
                >
                  {{ eventPrice }}
                </span>
              </div>
            </div>

            <h3
              class="seminar-elearning-fi__title"
              :data-event-id="event.eventId"
            >
              {{ event.title }}
            </h3>

            <div
              v-if="event.teaser"
              class="seminar-elearning-fi__teaser"
            >
              <text-expander-fi
                :content="{ value: event.teaser }"
                :max-height="170"
                :expand-text="fields.expandLabel?.value"
                :reduce-text="fields.reduceLabel?.value"
              />
            </div>

            <button-fi
              v-if="fields.eLearningButtonText?.value"
              :class="{
                'button-fi__tertiary button-fi__xs-streched': true,
              }"
              :href="link"
              :target="loggedIn ? '_blank' : undefined"
              :rel="loggedIn ? 'opener' : undefined"
              :natural-events="loggedIn"
              @click="onSubscribe"
            >
              {{ fields.eLearningButtonText.value }}
            </button-fi>
          </div>
        </grid-columns-fi>
      </div>
    </grid-container-fi>
    <section-divider-fi
      size="small"
      :dark="dark"
    />
  </div>
</template>

<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import { ButtonFi, IconFi, LoadingIndicator } from 'atoms';
import { computed, onMounted, ref } from 'vue';

import CloudImageFi from '@/components/atoms/cloud-image-fi/CloudImageFi.vue';
import GridColumnsFi from '@/components/grid-fi/GridColumnsFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import TextExpanderFi from '@/components/text-expander-fi/TextExpanderFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useLogin } from '@/composables/login';
import { usePriceCurrency } from '@/composables/priceCurrency';
import GETELEARNINGPRICE from '@/graphql/getELearningPrice.gql';
import SectionDividerFi from '@/pages/section-divider-fi/SectionDividerFi.vue';
import { useProfileFlyoutStore } from '@/store/pinia/profile-flyout';

defineOptions({ name: 'SeminarElearningFi' });

const props = withDefaults(
  defineProps<{
    event?: any; // TODO: any
    dark?: boolean;
  }>(),
  {
    event: () => ({}),
    dark: true,
  },
);

const fields = useFields('SeminarsOverviewFiFields');
const fischer = useFischer();
const { isLG } = useBreakpoints();
const { initializeAuthorization } = useLogin();
const { formatPrice } = usePriceCurrency();
const handle = useClientHandle();
const { preventProfileFlyout } = useProfileFlyoutStore();

const eventPrice = ref<string | null>(null);
const eventPriceLoading = ref(true);

const loggedIn = computed(() => fischer.user.authenticated());
const link = computed(() => `${fischer.uri()}#elearning:${(props.event as any).eventId}`);

onMounted(() => {
  const event = props.event as any;
  const hashLearning = /#elearning:(.*)/.exec(window.location.hash)?.[1];
  if (hashLearning !== event.eventId) {
    return;
  }
  if (fischer.user.authenticated()) {
    console.debug('redirecting to e-learning from hash');
    window.location.href = event.link;
  } else {
    console.debug('loggin-in to e-learning from hash');
    preventProfileFlyout();
    initializeAuthorization(true, `elearning:${event.eventId}`);
  }
});

const onSubscribe = () => {
  if (loggedIn.value) {
    return;
  }
  preventProfileFlyout();
  initializeAuthorization(true, `elearning:${(props.event as any).eventId}`);
};

const onIntersect = async () => {
  try {
    const { data, error } = await handle.client
      .query(
        GETELEARNINGPRICE,
        {
          assignmentId: (props.event as any).assignmentId,
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise();

    if (error) {
      eventPriceLoading.value = false;
      console.error(error);
      return;
    }

    const getELearningPrice = data?.getELearningPrice;
    if (getELearningPrice?.salesPrice) {
      if (getELearningPrice.salesPrice.feeElearning > 0) {
        eventPrice.value = formatPrice(
          getELearningPrice.salesPrice.feeElearning,
          getELearningPrice.salesPrice.currency || 'EUR',
        );
      } else {
        eventPrice.value = fields.freeOfChargeLabel?.value ?? null;
      }
    } else {
      eventPrice.value = null;
    }
    eventPriceLoading.value = false;
  } catch (error) {
    eventPriceLoading.value = false;
    console.error(error);
  }
};
</script>

<style scoped src="./seminar-elearning-fi" />
